import { v4 as uuidv4 } from "uuid";
import { EmailProvider, EmailTokens, EmailRecord } from "@/types/email";

// Load providers from localStorage
export const loadProviders = (): EmailProvider[] => {
  const savedProviders = localStorage.getItem("email_providers");
  if (savedProviders) {
    return JSON.parse(savedProviders);
  }
  return [];
};

// Save providers to localStorage
export const saveProviders = (providers: EmailProvider[]): void => {
  localStorage.setItem("email_providers", JSON.stringify(providers));
};

// Save a single provider to localStorage
export const saveProvider = (provider: EmailProvider): void => {
  const providers = loadProviders();
  const existingIndex = providers.findIndex(p => p.name === provider.name);
  
  if (existingIndex >= 0) {
    providers[existingIndex] = provider;
  } else {
    providers.push(provider);
  }
  
  saveProviders(providers);
  
  // Also save to individual localStorage key for backwards compatibility
  localStorage.setItem(`${provider.name}_provider`, JSON.stringify(provider));
};

// Get token for a specific provider
export const getProviderToken = (providerId: string): string => {
  const providers = loadProviders();
  const provider = providers.find((p) => p.name === providerId);
  return provider?.token || "";
};

// Save token for a specific provider
export const saveProviderToken = (providerId: string, token: string): void => {
  const providers = loadProviders();
  const index = providers.findIndex((p) => p.name === providerId);
  
  if (index !== -1) {
    providers[index] = { ...providers[index], token };
  } else {
    providers.push({
      id: providerId,
      name: providerId === "gmail" ? "gmail" : providerId === "outlook" ? "outlook" : providerId === "openai" ? "openai" : "perplexity",
      token,
      isConnected: true
    } as EmailProvider);
  }
  
  saveProviders(providers);
};

// Check if an investor has been emailed by checking localStorage or provided emails array
export const hasInvestorBeenEmailed = (investorId: string, emails?: any[]): boolean => {
  // If emails array is provided, use it instead of loading from localStorage
  if (emails) {
    return emails.some(email => 
      email.investorId === investorId && 
      email.status === "sent" && 
      email.sentAt
    );
  }
  
  // Otherwise check localStorage
  const savedEmails = localStorage.getItem("emails_data");
  if (savedEmails) {
    try {
      const emails = JSON.parse(savedEmails);
      return emails.some((email: any) => 
        email.investorId === investorId && 
        email.status === "sent" && 
        email.sentAt
      );
    } catch (e) {
      console.error("Error parsing emails from localStorage:", e);
      return false;
    }
  }
  
  return false;
};

// Add a sent email to localStorage
export const addSentEmail = (investorId: string, contactId: string, emailContent: string): void => {
  const savedEmails = localStorage.getItem("emails_data") || "[]";
  try {
    const emails = JSON.parse(savedEmails);
    emails.push({
      id: uuidv4(),
      investorId,
      contactId,
      subject: "Introduction Request",
      body: emailContent,
      sentAt: new Date().toISOString(),
      sentVia: "manual",
      hasResponse: false,
      status: "sent"
    });
    localStorage.setItem("emails_data", JSON.stringify(emails));
  } catch (e) {
    console.error("Error adding sent email:", e);
  }
};

// Update email with response information
export const updateEmailWithResponse = (emailId: string, hasResponse: boolean, responseNotes: string): EmailRecord | null => {
  const savedEmails = localStorage.getItem("emails_data");
  if (!savedEmails) return null;
  
  try {
    const emails = JSON.parse(savedEmails);
    const emailIndex = emails.findIndex((email: EmailRecord) => email.id === emailId);
    
    if (emailIndex === -1) return null;
    
    const updatedEmail = {
      ...emails[emailIndex],
      hasResponse,
      responseNotes,
      responseAt: hasResponse ? new Date().toISOString() : null
    };
    
    emails[emailIndex] = updatedEmail;
    localStorage.setItem("emails_data", JSON.stringify(emails));
    
    return updatedEmail;
  } catch (e) {
    console.error("Error updating email with response:", e);
    return null;
  }
};
